// =============================================
// VERIFICACIÓN DE COMPROBANTES (ADMIN)
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  TableSortLabel,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Visibility as ViewIcon,
  Description as FileIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const AdminReceiptVerification = () => {
  const [receipts, setReceipts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtros
  const [currentTab, setCurrentTab] = useState('pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Ordenamiento
  const [orderBy, setOrderBy] = useState('payment_date');
  const [order, setOrder] = useState('desc');

  // Diálogos
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadReceipts();
    loadStats();
  }, [currentTab]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const status = currentTab === 'all' ? '' : currentTab;
      const response = await api.get(`/payment-receipts?status=${status}`);
      setReceipts(response.data.receipts || []);
    } catch (err) {
      console.error('Error al cargar comprobantes:', err);
      setError('Error al cargar comprobantes');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/payment-receipts/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const handleOpenVerify = (receipt) => {
    setSelectedReceipt(receipt);
    setVerificationNotes('');
    setOpenVerifyDialog(true);
  };

  const handleOpenReject = (receipt) => {
    setSelectedReceipt(receipt);
    setRejectionReason('');
    setOpenRejectDialog(true);
  };

  const handleVerifyReceipt = async () => {
    try {
      setLoading(true);
      setError('');

      await api.put(`/payment-receipts/${selectedReceipt.id}/verify`, {
        verification_notes: verificationNotes,
      });

      setSuccess(`Comprobante #${selectedReceipt.id} verificado y pago aplicado`);
      setOpenVerifyDialog(false); // Cerrar diálogo
      setSelectedReceipt(null);
      loadReceipts();
      loadStats();
    } catch (err) {
      setError('Error al verificar: ' + (err.response?.data?.message || err.message));
      setOpenVerifyDialog(false); // ← AGREGAR: Cerrar incluso si hay error
    } finally {
      setLoading(false);
    }
  };

  const handleRejectReceipt = async () => {
    try {
      if (!rejectionReason.trim()) {
        setError('Debe proporcionar una razón de rechazo');
        return;
      }

      setLoading(true);
      setError('');

      await api.put(`/payment-receipts/${selectedReceipt.id}/reject`, {
        rejection_reason: rejectionReason,
      });

      setSuccess(`Comprobante #${selectedReceipt.id} rechazado`);
      setOpenRejectDialog(false);
      setSelectedReceipt(null);
      loadReceipts();
      loadStats();
    } catch (err) {
      setError('Error al rechazar: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id, originalName) => {
    try {
      const response = await api.get(`/payment-receipts/${id}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error al descargar comprobante');
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    // Manejar nulos
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    // Convertir a número si es posible para ordenamiento natural en house_number
    if (orderBy === 'house_number') {
      const aNum = parseInt(aVal);
      const bNum = parseInt(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        aVal = aNum;
        bVal = bNum;
      }
    }

    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-PA');
  };

  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { icon: <PendingIcon />, color: 'warning', label: 'Pendiente' },
      verified: { icon: <CheckIcon />, color: 'success', label: 'Verificado' },
      rejected: { icon: <CancelIcon />, color: 'error', label: 'Rechazado' },
    };
    return statusMap[status] || statusMap.pending;
  };

  const paginatedReceipts = [...receipts]
    .sort(getComparator(order, orderBy))
    .slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Verificación de Comprobantes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revisa y aprueba los pagos subidos por los propietarios
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => { loadReceipts(); loadStats(); }}
        >
          Actualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Pendientes
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatAmount(stats.pending_amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Verificados
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.verified}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatAmount(stats.verified_amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Rechazados
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.rejected}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total
                </Typography>
                <Typography variant="h4">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab
            label={
              <Badge badgeContent={stats?.pending || 0} color="warning">
                <span style={{ marginRight: 20 }}>Pendientes</span>
              </Badge>
            }
            value="pending"
          />
          <Tab label="Verificados" value="verified" />
          <Tab label="Rechazados" value="rejected" />
          <Tab label="Todos" value="all" />
        </Tabs>
      </Paper>

      {/* Tabla */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Propietario</strong></TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'house_number'}
                    direction={orderBy === 'house_number' ? order : 'asc'}
                    onClick={() => handleRequestSort('house_number')}
                  >
                    <strong>Casa</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? order : 'asc'}
                    onClick={() => handleRequestSort('amount')}
                  >
                    <strong>Monto</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'payment_date'}
                    direction={orderBy === 'payment_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('payment_date')}
                  >
                    <strong>Fecha Pago</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell><strong>Método</strong></TableCell>
                <TableCell><strong>Período</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedReceipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">
                      No hay comprobantes en esta categoría
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReceipts.map((receipt) => {
                  const statusInfo = getStatusInfo(receipt.status);
                  return (
                    <TableRow key={receipt.id}>
                      <TableCell>#{receipt.id}</TableCell>
                      <TableCell>{receipt.user_name}</TableCell>
                      <TableCell>{receipt.house_number}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatAmount(receipt.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(receipt.payment_date)}</TableCell>
                      <TableCell>{receipt.payment_method}</TableCell>
                      <TableCell>
                        {receipt.application_type === 'distribute' ? (
                          <Chip label="Abono" size="small" variant="outlined" color="info" />
                        ) : receipt.month && receipt.year ? (
                          `${receipt.month}/${receipt.year}`
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(receipt.id, receipt.receipt_original_name)}
                            title="Descargar"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          {receipt.status === 'pending' && (
                            <>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleOpenVerify(receipt)}
                                title="Verificar"
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenReject(receipt)}
                                title="Rechazar"
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={receipts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Diálogo Verificar */}
      <Dialog open={openVerifyDialog} onClose={() => setOpenVerifyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verificar Comprobante</DialogTitle>
        <DialogContent>
          {selectedReceipt && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Al verificar, se creará automáticamente un pago y se aplicará al período correspondiente.
              </Alert>

              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Propietario:</Typography>
                    <Typography variant="body2"><strong>{selectedReceipt.user_name}</strong></Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Monto:</Typography>
                    <Typography variant="body2"><strong>{formatAmount(selectedReceipt.amount)}</strong></Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Tipo:</Typography>
                    <Typography variant="body2">
                      <strong>
                        {selectedReceipt.application_type === 'distribute'
                          ? '💰 Abono (distribuir)'
                          : `📅 Pago específico (${selectedReceipt.month}/${selectedReceipt.year})`}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Referencia:</Typography>
                    <Typography variant="body2">{selectedReceipt.reference_number || '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notas de Verificación (Opcional)"
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="Ej: Verificado en extracto bancario del 15/01/2026"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVerifyDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleVerifyReceipt}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? 'Verificando...' : 'Verificar y Aplicar Pago'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Rechazar */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rechazar Comprobante</DialogTitle>
        <DialogContent>
          {selectedReceipt && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                El propietario será notificado del rechazo y podrá subir un nuevo comprobante.
              </Alert>

              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Razón del Rechazo"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Ej: El comprobante no es legible, falta información de referencia, etc."
                error={!rejectionReason.trim() && rejectionReason !== ''}
                helperText="Este mensaje será visible para el propietario"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleRejectReceipt}
            variant="contained"
            color="error"
            disabled={loading || !rejectionReason.trim()}
          >
            {loading ? 'Rechazando...' : 'Rechazar Comprobante'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminReceiptVerification;
